import type { Itinerary } from '../../services/api'
import { API_BASE_URL } from '../../services/env'

/**
 * Write an itinerary into the 'trip-itinerary' source: walk legs as 2-point dashed
 * lines and dots at the trip's ends plus every transfer station. Ride legs draw no
 * line of their own (straight stop-to-stop chords cut across the street grid and
 * read as a phantom route next to the real one) because trip focus mode (see
 * tripRouteIds) already draws each ridden line's true route shape; they still count
 * toward the framing bounds. `fit` frames the whole trip, padded for the half-open
 * Plan sheet below.
 */
export function applyItinerary(map: any, itinerary: Itinerary | null, fit: boolean): void {
  const src = map.getSource('trip-itinerary') as any
  if (!src) return

  if (!itinerary) {
    src.setData({ type: 'FeatureCollection', features: [] })
    return
  }

  const features: GeoJSON.Feature[] = []
  const bounds: [[number, number], [number, number]] = [[Infinity, Infinity], [-Infinity, -Infinity]]
  const extend = (lon: number, lat: number) => {
    bounds[0][0] = Math.min(bounds[0][0], lon); bounds[0][1] = Math.min(bounds[0][1], lat)
    bounds[1][0] = Math.max(bounds[1][0], lon); bounds[1][1] = Math.max(bounds[1][1], lat)
  }

  for (const leg of itinerary.legs) {
    const coords: [number, number][] = leg.mode === 'walk'
      ? [[leg.from.lon, leg.from.lat], [leg.to.lon, leg.to.lat]]
      : leg.stops.map((s) => [s.lon, s.lat] as [number, number])
    for (const [lon, lat] of coords) extend(lon, lat)
    if (leg.mode === 'walk') {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { mode: 'walk' },
      })
    }
  }

  const first = itinerary.legs[0]
  const last = itinerary.legs[itinerary.legs.length - 1]
  const point = (lon: number, lat: number, kind: 'end' | 'transfer'): GeoJSON.Feature => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [lon, lat] },
    properties: { kind },
  })
  features.push(point(first.from.lon, first.from.lat, 'end'))
  features.push(point(last.to.lon, last.to.lat, 'end'))
  for (const leg of itinerary.legs) {
    if (leg.mode === 'walk') continue
    features.push(point(leg.from.lon, leg.from.lat, 'transfer'))
    features.push(point(leg.to.lon, leg.to.lat, 'transfer'))
  }

  src.setData({ type: 'FeatureCollection', features })

  if (fit && Number.isFinite(bounds[0][0])) {
    map.fitBounds(bounds, {
      padding: {
        top: 70,
        // Keep the trip visible above the half-open Plan sheet.
        bottom: Math.round((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.5) + 40,
        left: 45,
        right: 45,
      },
      maxZoom: 15,
      duration: 900,
    })
  }
}
// The basemap style URLs live in `mapTheme.ts`; there are two of them now (light and
// dark), and which one is up is a user setting rather than a constant.
export const NYC_CENTER: [number, number] = [-73.9857, 40.7484]
export const NYC_ZOOM = 13
// '' in web dev (Vite proxies /api); the baked-in backend origin in native builds, where
// relative paths would hit Capacitor's local asset server instead of the backend.
export const BASE_URL = API_BASE_URL

